// Donut — thin-ring chart used by the Home "My access" card.
// design ref: components.jsx Donut() + styles.css:668–703 (.donut / .donut__chart
// / .donut__center). r=40, stroke=6, viewBox 90×90, track neutral.100, segments
// rounded-cap. The <svg> is rotated -90deg so the first segment starts at 12
// o'clock. Center shows the total in the display font at 32px.

import { Box } from "@chakra-ui/react";

export interface DonutSegment {
  value: number;
  /** A resolved CSS color (theme token resolved by the caller, or a hex). */
  color: string;
}

export interface DonutProps {
  segments: DonutSegment[];
  total: number | string;
}

const R = 40;
const STROKE = 6;
const C = 2 * Math.PI * R;

const Donut = ({ segments, total }: DonutProps) => {
  const sum = segments.reduce((s, x) => s + x.value, 0) || 1;
  let offset = 0;

  return (
    <Box position="relative" w="90px" h="90px" flexShrink={0}>
      <Box
        as="svg"
        viewBox="0 0 90 90"
        w="100%"
        h="100%"
        transform="rotate(-90deg)"
      >
        {/* design ref: styles.css:743 — track ring */}
        <circle
          cx="45"
          cy="45"
          r={R}
          fill="none"
          stroke="var(--chakra-colors-neutral-100)"
          strokeWidth={STROKE}
        />
        {segments.map((s, i) => {
          const dash = (s.value / sum) * C;
          const el = (
            <circle
              key={i}
              cx="45"
              cy="45"
              r={R}
              fill="none"
              stroke={s.color}
              strokeWidth={STROKE}
              strokeDasharray={`${dash} ${C - dash}`}
              strokeDashoffset={-offset}
              strokeLinecap="round"
            />
          );
          offset += dash;
          return el;
        })}
      </Box>
      {/* design ref: styles.css:680–687 — centered total, display font 32px */}
      <Box
        position="absolute"
        inset={0}
        display="flex"
        alignItems="center"
        justifyContent="center"
        fontFamily="heading"
        fontSize="3xl"
        fontWeight={500}
        color="neutral.950"
      >
        {total}
      </Box>
    </Box>
  );
};

export default Donut;
